import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { put, takeLatest, call, select, takeEvery, all, delay } from 'redux-saga/effects';
import findIndex from 'lodash/findIndex';
import findLastIndex from 'lodash/findLastIndex';
import { isApiError, requestPuppy } from '@wix/da-http-client';
import { changeOrder, threadUpdated, initializeThread, streamIdChanged, fetchNextInThread, fetchPreviousInThread } from './actionCreators';
import { selectors, actions, withCursor } from '../Stream';
import { getStreamId, updateCommentOffset } from './helpers';
import { getCurrentUserCommentsOrder, getIsMobile } from '../publicSession/selectors';
import { updateCommentsOrder } from '../publicSession/actions';
import { THREAD_MAX_DEPTH, THREAD_MAX_DEPTH_MOBILE, COMMENT_THREAD_STREAM_GROUP_ID, DEFAULT_ITEMS_PER_FETCH } from './constants';
import { getThreadState } from './selectors';
import { FetchType } from '../Stream/base/types';
import takeLatestBy from '../redux/effects/takeLatestBy';
function fakeResponse(options) {
    if (process.env.NODE_ENV !== 'production') {
        if (window.doError) {
            return {
                error: 'invalid_request',
                errorDescription: 'Not logged in.',
                status: 'error'
            };
        }
        // eslint-disable-next-line
        const Fake = require('@wix/da-sample-data').Fake;
        const fakeThread = Fake.makeCommentThread(48, false);
        fakeThread.cursor = fakeThread.thread[fakeThread.thread.length - 1]['commentId'];
        if (options.params.order === 'oldest') {
            const fake = _object_spread({}, fakeThread);
            fake.thread = [
                ...fakeThread.thread
            ];
            fake.thread.reverse();
            return fake;
        }
        return fakeThread;
    }
}
/**
 * TODO this should probably be the selector and also better to have stream type in
 * streamParams and check that...
 * @param streamId
 */ function* getStreamParams(streamId) {
    const streamParams = yield select(selectors.getStreamParams, streamId);
    if (!streamParams) {
        return false;
    }
    const { commentableTypeId, commentableItemId } = streamParams;
    if (!commentableTypeId || !commentableItemId) {
        return false;
    }
    return streamParams;
}
function* fetchThreadWithCursor({ commentableTypeId, commentableItemId, parentId, order, maxDepth }, cursor, limit) {
    const params = {
        typeid: commentableTypeId,
        itemid: commentableItemId,
        parentid: parentId,
        maxdepth: maxDepth,
        order,
        cursor,
        limit
    };
    const result = yield call(requestPuppy, {
        method: 'get',
        url: '/comments/thread',
        params
    }, fakeResponse);
    return result;
}
function* fetchMoreCommentsWithCursor({ payload: { streamId } }) {
    yield delay(300);
    const streamParams = yield call(getStreamParams, streamId);
    const { cursor, limit } = yield select((state)=>({
            cursor: withCursor.selectors.getCursor(state, streamId),
            limit: selectors.getItemsPerFetch(state, streamId)
        }));
    const result = yield call(fetchThreadWithCursor, streamParams, cursor, limit);
    if (!result || isApiError(result)) {
        // TODO: request does not return error message atm
        yield put(withCursor.actions.fetchFailure({
            streamId,
            fetchType: FetchType.Next,
            errorMsg: 'Failed to fetch'
        }));
        return;
    }
    var _result_total;
    yield put(withCursor.actions.fetchSuccess({
        streamId,
        hasMore: !!result.hasMore,
        items: result.thread,
        cursor: result.cursor || undefined,
        total: (_result_total = result.total) !== null && _result_total !== void 0 ? _result_total : undefined
    }));
}
function* fetchPreviousComments({ payload: { streamId } }) {
    yield delay(300);
    const streamParams = yield call(getStreamParams, streamId);
    const { cursor, limit } = yield select((state)=>({
            cursor: withCursor.selectors.getPrevCursor(state, streamId),
            limit: selectors.getItemsPerFetch(state, streamId)
        }));
    const result = yield call(fetchThreadWithCursor, streamParams, cursor, limit);
    if (!result || isApiError(result)) {
        // TODO: request does not return error message atm
        yield put(withCursor.actions.fetchFailure({
            streamId,
            fetchType: FetchType.Previous,
            errorMsg: 'Failed to fetch'
        }));
        return;
    }
    yield put(withCursor.actions.fetchPreviousSuccess({
        streamId,
        hasLess: !!result.hasLess,
        items: result.thread,
        prevCursor: result.prevCursor || undefined
    }));
    if (result.thread.length > 0) {
        updateCommentOffset(result.thread[0]);
    }
}
function* switchStreamOrder(action) {
    if (!action.payload) {
        return;
    }
    const { streamId, order } = action.payload;
    const streamParams = yield call(getStreamParams, streamId);
    if (!streamParams) {
        return;
    }
    const itemsPerFetch = yield select(selectors.getItemsPerFetch, streamId);
    const result = yield call(fetchThreadWithCursor, _object_spread_props(_object_spread({}, streamParams), {
        order
    }), undefined, itemsPerFetch);
    if (!result || isApiError(result)) {
        yield put(// TODO: request does not return error message atm
        withCursor.actions.fetchFailure({
            streamId,
            fetchType: FetchType.Next,
            errorMsg: 'Failed to fetch'
        }));
        return;
    }
    const newStreamParams = _object_spread_props(_object_spread({}, streamParams), {
        order
    });
    yield all([
        put(withCursor.actions.initialize({
            streamId,
            streamParams: newStreamParams,
            itemsPerFetch,
            items: result.thread,
            hasMore: result.hasMore,
            total: result.total,
            cursor: result.cursor,
            fetchNextCallback: fetchNextInThread,
            fetchPreviousCallback: fetchPreviousInThread
        })),
        put(updateCommentsOrder(order))
    ]);
    if (result.thread.length > 0) {
        updateCommentOffset(result.thread[0]);
    }
}
function* handleThreadUpdate(action) {
    if (!action.payload) {
        return;
    }
    const { streamId, result } = action.payload;
    const streamParams = yield call(getStreamParams, streamId);
    if (!streamParams) {
        return;
    }
    const items = yield select(selectors.getItems, streamId);
    if (!result || !result.comment || isApiError(result)) {
        return;
    }
    // Does it already exist? If so it's an edit and we replace the item
    if (findIndex(items, {
        commentId: result.comment.commentId
    }) !== -1) {
        yield put(withCursor.actions.replaceItem({
            streamId,
            newItem: result.comment,
            predicate: {
                commentId: result.comment.commentId
            }
        }));
        return;
    }
    const newComment = _object_spread_props(_object_spread({}, result.comment), {
        isNew: true
    });
    yield put(actions.setParam({
        streamId,
        paramType: 'lastInsertedCommentId',
        paramValue: newComment.commentId
    }));
    const getNewCursor = (newOffset, comment, stack)=>{
        return newOffset >= stack.length ? comment.commentId : undefined;
    };
    // It's a new comment. Add it to the root which is HEAD/FOOT depending
    // on which order is selected.
    // Consider it "new" when new comment has same parent as the thread - this
    // means we are viewing thread from some depth inside the main thread
    if (!result.comment.parentId || parseInt(result.comment.parentId) === parseInt(streamParams.parentId)) {
        // Cursor is the newComment id when oldest sort
        const offset = streamParams.order === 'oldest' ? items.length : 0;
        yield put(withCursor.actions.insertItemsAtOffsetAndSetCursor({
            streamId,
            items: [
                newComment
            ],
            offset,
            newCursor: getNewCursor(offset, newComment, items)
        }));
        return;
    }
    // For newest sort order we can simply find the comment the reply was for
    // and insert into one ahead
    if (streamParams.order === 'newest') {
        const repliedToItemIndex = findIndex(items, {
            commentId: result.comment.parentId
        });
        if (repliedToItemIndex === -1) {
            // The parent isn't in items, so we want to just append this to the start of the comment thread
            // This normally happens on pages dedicated to a specific parent comment
            yield put(withCursor.actions.insertItemsAtOffsetAndSetCursor({
                streamId,
                items: [
                    newComment
                ],
                offset: 0,
                newCursor: getNewCursor(0, newComment, items)
            }));
            return;
        } else {
            // cursor is new comment when its at the end of the stack
            const offset = repliedToItemIndex + 1;
            yield put(withCursor.actions.insertItemsAtOffsetAndSetCursor({
                streamId,
                items: [
                    newComment
                ],
                offset,
                newCursor: getNewCursor(offset, newComment, items)
            }));
            return;
        }
    }
    // For oldest things are more complex we need to find the parent
    // and then add out item to the end of the reply thread.
    // Note this ignores pagination boundaries (just like current site)
    // this makes sense else the reply would seem to disappear to the user
    if (streamParams.order === 'oldest') {
        const lastReplyToParentIndex = findLastIndex(items, {
            parentId: result.comment.parentId
        });
        // No other replies - add to parent
        if (lastReplyToParentIndex === -1) {
            const repliedToItemIndex = findIndex(items, {
                commentId: result.comment.parentId
            });
            if (repliedToItemIndex === -1) {
                // The parent isn't in items, so we want to just append this to the start of the comment thread
                // This normally happens on pages dedicated to a specific parent comment
                yield put(withCursor.actions.insertItemsAtOffsetAndSetCursor({
                    streamId,
                    items: [
                        newComment
                    ],
                    offset: items.length,
                    newCursor: getNewCursor(items.length, newComment, items)
                }));
                return;
            } else {
                const offset = repliedToItemIndex + 1;
                yield put(withCursor.actions.insertItemsAtOffsetAndSetCursor({
                    streamId,
                    items: [
                        newComment
                    ],
                    offset,
                    newCursor: getNewCursor(offset, newComment, items)
                }));
            }
        } else {
            const offset = lastReplyToParentIndex + 1;
            yield put(withCursor.actions.insertItemsAtOffsetAndSetCursor({
                streamId,
                items: [
                    newComment
                ],
                offset,
                newCursor: getNewCursor(offset, newComment, items)
            }));
        }
    }
}
function* handleStreamIdChanged(action) {
    const userOrder = yield select(getCurrentUserCommentsOrder);
    const { streamId, commentableItemId, commentableTypeId, maxDepth, parentId, order = userOrder } = action.payload;
    if (!streamId) {
        return;
    }
    const isInitialized = yield select(selectors.isInitialized, streamId);
    if (!isInitialized) {
        // commentModals are initialized here as a branch of a parent thread
        // so we steal some of the stream params of the parent thread
        const parentThreadState = parentId ? yield select(getThreadState, commentableTypeId, commentableItemId) : {};
        const streamParams = {
            order,
            commentableTypeId,
            commentableItemId,
            parentId,
            canPostComment: parentThreadState.canPostComment,
            maxDepth
        };
        const itemsPerFetch = parentThreadState.itemsPerFetch || DEFAULT_ITEMS_PER_FETCH;
        yield put(withCursor.actions.initialize({
            streamId,
            streamParams,
            itemsPerFetch,
            items: [],
            hasMore: true,
            fetchNextCallback: fetchNextInThread,
            fetchPreviousCallback: fetchPreviousInThread
        }));
    }
    // add check:
    // no items, has more
    const hasMore = yield select(selectors.hasMore, streamId);
    const items = yield select(selectors.getItems, streamId);
    const noItems = items.length === 0;
    if (noItems && hasMore) {
        yield put(actions.fetchNext(streamId));
    }
}
function* handleThreadInit(action) {
    const { thread } = action.payload;
    if (!thread) {
        return;
    }
    const isMobile = yield select(getIsMobile);
    const defaultMaxDepth = isMobile ? THREAD_MAX_DEPTH_MOBILE : THREAD_MAX_DEPTH;
    const { maxDepth = defaultMaxDepth, itemsPerFetch = DEFAULT_ITEMS_PER_FETCH } = action.payload;
    const userOrder = yield select(getCurrentUserCommentsOrder);
    const { commentableTypeId, commentableItemId, parentId, total, items, hasMore, hasLess, canPostComment, order = userOrder, cursor, prevCursor } = thread;
    const streamParams = {
        order,
        commentableTypeId,
        commentableItemId,
        parentId,
        canPostComment,
        maxDepth
    };
    yield put(withCursor.actions.initialize({
        streamId: getStreamId(commentableTypeId, commentableItemId, parentId),
        groupId: COMMENT_THREAD_STREAM_GROUP_ID,
        streamParams,
        itemsPerFetch,
        items,
        hasMore,
        hasLess,
        total,
        cursor,
        prevCursor,
        fetchNextCallback: fetchNextInThread,
        fetchPreviousCallback: fetchPreviousInThread
    }));
}
export default function* saga() {
    yield all([
        takeEvery(streamIdChanged, handleStreamIdChanged),
        takeEvery(initializeThread, handleThreadInit),
        takeLatestBy(fetchNextInThread, fetchMoreCommentsWithCursor, (action)=>action.payload.streamId),
        takeLatest(fetchPreviousInThread, fetchPreviousComments),
        takeLatest(changeOrder, switchStreamOrder),
        takeLatest(threadUpdated, handleThreadUpdate)
    ]);
}

//# sourceMappingURL=saga.js.map